// Primitivos visuais do Dashboard de Métricas.
// Um único lugar para: KPI, faixa de KPIs, barra com meta, sparkline,
// barra empilhada, linha de insight e estados vazios.
//
// Regras (UI-UX.md):
// - Nenhum número aparece sem contexto (delta, denominador, meta ou tendência).
// - Ausência de dado é `—`, nunca `0`.
// - Todo número usa tabular-nums.

import { useEffect, useState } from 'react';
import Iconify from 'src/components/Iconify';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'src/@/components/ui/tooltip';
import { PAINEL_DESIGN as D, T, TONES, SPARK_RAMP } from 'src/pages/dashboard/Painel/Painel.design';

// ---------------------------------------------------------------- formatação

/**
 * `null`/`undefined`/`''` significam "sem dado" e devem virar `—`, não `0`.
 * Sem esta guarda, `Number(null) === 0` transformaria ausência em zero real.
 */
const semDado = (v) => v === null || v === undefined || v === '' || Number.isNaN(Number(v));

export function fmtInt(value) {
    if (semDado(value)) return null;
    return Number(value).toLocaleString('pt-BR');
}

/** Segundos -> "42s" | "2m 14s" | "1h 12m". Nunca devolve número puro. */
export function fmtSecs(seconds) {
    if (semDado(seconds)) return null;
    const s = Number(seconds);
    if (!Number.isFinite(s) || s < 0) return null;
    if (s < 60) return `${Math.round(s)}s`;
    if (s < 3600) {
        const m = Math.floor(s / 60);
        const rest = Math.round(s % 60);
        return rest ? `${m}m ${String(rest).padStart(2, '0')}s` : `${m}m`;
    }
    const h = Math.floor(s / 3600);
    const m = Math.round((s % 3600) / 60);
    return m ? `${h}h ${String(m).padStart(2, '0')}m` : `${h}h`;
}

/** Valor monetário compacto: R$ 1,84 mi · R$ 19,8k · R$ 412 */
export function fmtBRL(value, { compact = true } = {}) {
    if (semDado(value)) return null;
    const v = Number(value);
    if (!Number.isFinite(v)) return null;
    if (!compact) return `R$ ${v.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}`;
    const abs = Math.abs(v);
    if (abs >= 1e6) return `R$ ${(v / 1e6).toLocaleString('pt-BR', { maximumFractionDigits: 2 })} mi`;
    if (abs >= 1e3) return `R$ ${(v / 1e3).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}k`;
    return `R$ ${v.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}`;
}

export function fmtPct(value, decimals = 1) {
    if (semDado(value)) return null;
    const v = Number(value);
    if (!Number.isFinite(v)) return null;
    return `${v.toLocaleString('pt-BR', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}%`;
}

export function ratio(part, total) {
    if (semDado(part) || semDado(total)) return null;
    const p = Number(part);
    const t = Number(total);
    if (!Number.isFinite(p) || !Number.isFinite(t) || t <= 0) return null;
    return (p / t) * 100;
}

/** Variação percentual entre dois períodos. null quando a base é zero. */
export function delta(current, previous) {
    const c = Number(current);
    const p = Number(previous);
    if (!Number.isFinite(c) || !Number.isFinite(p) || p === 0) return null;
    return ((c - p) / Math.abs(p)) * 100;
}

const clampPct = (n) => Math.max(0, Math.min(100, Number(n) || 0));

// ------------------------------------------------------------------ átomos

/** Ausência de dado. Distingue "sem dado" de "zero real". */
export function Dash() {
    return <span style={{ color: T.empty }}>—</span>;
}

export function Badge({ tone = 'neutral', children }) {
    const t = TONES[tone] || TONES.neutral;
    return (
        <span
            className="px-[7px] py-0.5 rounded-[5px] text-[10.5px] font-extrabold uppercase tracking-[0.04em]"
            style={{ background: t.badgeBg, color: t.badgeText }}
        >
            {children}
        </span>
    );
}

/** Delta vs. período anterior. `goodWhenDown` inverte a leitura de cor. */
export function Delta({ value, goodWhenDown = false, size = 13 }) {
    if (value === null || value === undefined || !Number.isFinite(Number(value))) return null;
    const v = Number(value);
    const up = v > 0;
    const flat = Math.abs(v) < 0.5;
    const good = flat ? null : goodWhenDown ? !up : up;
    const color = good === null ? T.label : good ? T.ok : T.crit;
    return (
        <div className="flex items-center gap-1 font-bold tabular-nums" style={{ fontSize: size, color }}>
            {!flat && (
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round">
                    <path d={up ? 'M7 14l5-5 5 5' : 'M7 10l5 5 5-5'} />
                </svg>
            )}
            {`${Math.abs(v).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`}
        </div>
    );
}

/**
 * Tendência de 10 pontos (ou menos). Não é para leitura precisa — só direção.
 * Recebe números crus; normaliza internamente pelo máximo.
 */
export function Sparkline({ values = [], tone = 'neutral', height = 34 }) {
    const nums = (values || []).map((v) => Number(v) || 0);
    if (nums.length < 2) return null;
    const max = Math.max(...nums, 1);
    const ramp = SPARK_RAMP[tone] || SPARK_RAMP.neutral;
    const last = nums.length - 1;
    return (
        <div className="flex items-end gap-[3px]" style={{ height }}>
            {nums.map((n, i) => {
                const shade = i === last ? ramp[3] : ramp[Math.min(2, Math.floor((i / nums.length) * 3))];
                return (
                    <div
                        key={i}
                        className="flex-1 rounded-[2px]"
                        style={{ height: `${Math.max(4, (n / max) * 100)}%`, background: shade }}
                    />
                );
            })}
        </div>
    );
}

/**
 * Barra de progresso com marcador de meta.
 * `pct` e `targetPct` já em 0–100 sobre a mesma escala.
 */
export function Meter({ pct, targetPct = null, tone = 'neutral', left, right, height = 6 }) {
    const t = TONES[tone] || TONES.neutral;
    return (
        <div>
            <div className="relative rounded-full" style={{ height, background: T.track }}>
                <div className="h-full rounded-full" style={{ width: `${clampPct(pct)}%`, background: t.fill }} />
                {targetPct !== null && targetPct !== undefined && (
                    <div
                        className="absolute rounded-[2px]"
                        style={{ top: -4, left: `${clampPct(targetPct)}%`, width: 2, height: height + 8, background: T.text }}
                    />
                )}
            </div>
            {(left || right) && (
                <div className="flex justify-between gap-3 mt-2 text-[11.5px] font-semibold" style={{ color: T.muted }}>
                    <span style={left?.tone ? { color: TONES[left.tone].value } : undefined}>{left?.text ?? left}</span>
                    <span className="text-right">{right?.text ?? right}</span>
                </div>
            )}
        </div>
    );
}

/** Barra 100% empilhada. `segments`: [{ pct, color, label, textColor }] */
export function Stacked({ segments = [], height = 10, radius = 99, showLabels = false, minLabelPct = 6 }) {
    const visible = segments.filter((s) => Number(s.pct) > 0);
    if (!visible.length) {
        return <div className="rounded-full" style={{ height, background: T.track }} />;
    }
    return (
        <div className="flex overflow-hidden" style={{ height, borderRadius: radius === 99 ? 99 : radius, background: T.track }}>
            {visible.map((s, i) => (
                <div
                    key={i}
                    className="flex items-center justify-center font-extrabold"
                    style={{ width: `${clampPct(s.pct)}%`, background: s.color, color: s.textColor || '#fff', fontSize: 11.5 }}
                    title={s.label ? `${s.label}: ${Math.round(s.pct)}%` : undefined}
                >
                    {showLabels && s.pct >= minLabelPct ? `${Math.round(s.pct)}%` : ''}
                </div>
            ))}
        </div>
    );
}

/**
 * Colunas com escala única (nunca eixo duplo — séries de grandezas diferentes
 * vão em blocos separados).
 * `data`: [{ label, [key]: number }] · `series`: [{ key, label, color }]
 *
 * `stacked` empilha as séries dentro da mesma coluna em vez de dividir a largura.
 * Serve para composição de um total (dentro/fora do SLA, ganho/perda por período)
 * e é o que dá suporte ao `stacked_bar` que o agente de IA emite.
 */
export function ColumnChart({
    data = [],
    series = [],
    height = 180,
    formatValue = fmtInt,
    maxOverride,
    barWidth = 26,
    stacked = false,
    minColWidth = 34,
    labelStep = 1,
    labelTruncate = true,
    showAxis = false,
    fitWidth = true,
}) {
    if (!data.length || !series.length) return <Empty />;
    // Empilhado compara TOTAIS, então a escala é a soma da coluna; agrupado
    // compara séries entre si, então a escala é o maior valor individual. Usar a
    // escala errada achata o gráfico até virar ruído.
    const max = maxOverride || Math.max(
        1,
        ...(stacked
            ? data.map((d) => series.reduce((acc, s) => acc + (Number(d[s.key]) || 0), 0))
            : data.flatMap((d) => series.map((s) => Number(d[s.key]) || 0))),
    );
    const cols = fitWidth
        ? `repeat(${data.length}, minmax(0, 1fr))`
        : `repeat(${data.length}, minmax(${Math.max(20, minColWidth - 8)}px, 1fr))`;
    const step = Math.max(1, labelStep);
    const axisTicks = showAxis ? [max, Math.round(max / 2), 0] : [];
    const colGap = data.length > 20 ? 4 : 10;
    const effectiveBarWidth = fitWidth && data.length > 20 ? Math.min(barWidth, 14) : barWidth;

    const renderBars = () => (
        <div className="w-full" style={fitWidth ? undefined : { minWidth: data.length * minColWidth }}>
            <div
                className="grid items-end pt-2"
                style={{ gridTemplateColumns: cols, height, borderBottom: `1px solid ${T.border}`, gap: colGap }}
            >
                {data.map((d, i) => {
                    const tooltipLabel = d.fullLabel || d.label;
                    const tooltipValues = series.map((s) => `${s.label}: ${formatValue(d[s.key]) ?? '—'}`).join(' / ');
                    return (
                        <div
                            key={tooltipLabel || i}
                            className={`flex ${stacked ? 'flex-col justify-end items-center' : 'gap-[2px] items-end justify-center'} h-full min-w-0`}
                            title={`${tooltipLabel} · ${tooltipValues}`}
                        >
                            {(stacked ? [...series].reverse() : series).map((s, si) => (
                                <div
                                    key={s.key}
                                    className={`${stacked ? 'w-full' : 'w-full'} transition-all ${
                                        !stacked || si === series.length - 1 ? 'rounded-t-[2px]' : ''
                                    }`}
                                    style={{
                                        height: `${stacked
                                            ? ((Number(d[s.key]) || 0) / max) * 100
                                            : Math.max(1, ((Number(d[s.key]) || 0) / max) * 100)}%`,
                                        maxWidth: effectiveBarWidth,
                                        background: s.color,
                                    }}
                                />
                            ))}
                        </div>
                    );
                })}
            </div>
            <div
                className="grid mt-2 text-[10px] font-semibold text-center tabular-nums leading-tight"
                style={{ gridTemplateColumns: cols, color: T.muted, gap: colGap }}
            >
                {data.map((d, i) => {
                    const showLabel = i % step === 0 || i === data.length - 1;
                    const label = d.fullLabel || d.label;
                    return (
                        <div
                            key={label || i}
                            className={`min-w-0 ${labelTruncate ? 'truncate' : ''}`}
                            title={label}
                        >
                            {showLabel ? d.label : ''}
                        </div>
                    );
                })}
            </div>
        </div>
    );

    if (!showAxis) {
        return (
            <div className={fitWidth ? 'w-full' : 'overflow-x-auto'}>
                {renderBars()}
            </div>
        );
    }

    return (
        <div className={fitWidth ? 'w-full' : 'overflow-x-auto'}>
            <div className="flex gap-2 items-stretch min-w-0 w-full">
                <div
                    className="flex flex-col justify-between text-[10px] font-semibold tabular-nums flex-shrink-0 pb-7"
                    style={{ height, color: T.muted }}
                >
                    {axisTicks.map((tick) => (
                        <span key={tick}>{formatValue(tick) ?? '—'}</span>
                    ))}
                </div>
                <div className="flex-1 min-w-0">
                    {renderBars()}
                </div>
            </div>
        </div>
    );
}

export function Legend({ items = [] }) {
    return (
        <div className="flex flex-wrap items-center gap-x-[18px] gap-y-2 text-[11.5px] font-semibold" style={{ color: T.label }}>
            {items.map((it, i) => (
                <span key={i} className="flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-[2px]" style={{ background: it.color }} />
                    {it.label}
                </span>
            ))}
        </div>
    );
}

/** Linha rotulada com barra — usada em funil, origens, distribuições. */
export function BarRow({ label, value, pct, color = T.brand, right, height = 10, labelWidth }) {
    return (
        <div>
            <div className="flex items-baseline justify-between gap-3 mb-[7px]">
                <div className="text-[13px] font-bold truncate" style={{ color: T.text, width: labelWidth }}>
                    {label}
                </div>
                <div className="flex items-baseline gap-2.5 flex-shrink-0">
                    <span className="text-[15px] font-extrabold tabular-nums">{value}</span>
                    {right !== undefined && (
                        <span className="text-[11.5px] font-semibold w-[42px] text-right tabular-nums" style={{ color: T.muted }}>
                            {right}
                        </span>
                    )}
                </div>
            </div>
            <div className="rounded-full overflow-hidden" style={{ height, background: T.track }}>
                <div className="h-full rounded-full" style={{ width: `${clampPct(pct)}%`, background: color }} />
            </div>
        </div>
    );
}

/**
 * Funil de conversão HORIZONTAL contínuo (estilo funnel chart clássico):
 * trapézios emendados fluindo da esquerda para a direita, em que a aresta
 * direita de cada segmento tem a altura do segmento seguinte — a silhueta
 * afunila de verdade, sem blocos soltos. Rótulo vertical dentro do segmento
 * quando há altura; legenda completa abaixo (com a conversão para a etapa
 * seguinte, quando houver). Estágio zerado vira um estrangulamento visível.
 * `steps`: [{ label, value, color, conversao? (0–100 ou null) }]
 */
/** Fundos claros (fim da rampa de roxo) pedem texto escuro para contraste. */
function corTextoSobre(hex) {
    if (!hex || hex[0] !== '#' || hex.length !== 7) return '#fff';
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    const luma = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luma > 0.7 ? T.text : '#fff';
}

export function ConversionFunnel({ steps = [], formatValue = fmtInt, height = 220, showLegend = true }) {
    const validos = (steps || []).filter((s) => s && s.value !== null && s.value !== undefined);
    if (!validos.length) return <Empty />;
    const max = Math.max(...validos.map((s) => Number(s.value) || 0));
    if (max <= 0) return <Empty />;

    const n = validos.length;
    const SEG = 100; // largura de cada segmento em unidades do viewBox
    const GAP = 2; // fresta branca entre segmentos (a diagonal fica visível)
    const PAD_Y = 4;
    const innerH = height - PAD_Y * 2;
    const MIN_H = 7; // estágio zerado vira estrangulamento, não desaparece
    const cy = height / 2;

    // Altura de cada estágio; a aresta direita do segmento i é a altura do i+1,
    // o que emenda os trapézios num corpo contínuo que afunila.
    const alturas = validos.map((s) => Math.max(MIN_H, ((Number(s.value) || 0) / max) * innerH));

    return (
        <div>
            <div className="overflow-x-auto">
                <div className="relative w-full" style={{ height, minWidth: n * 56 }}>
                    <svg
                        className="absolute inset-0 w-full h-full"
                        viewBox={`0 0 ${n * SEG} ${height}`}
                        preserveAspectRatio="none"
                        aria-hidden="true"
                    >
                        {validos.map((s, i) => {
                            const a = alturas[i] / 2;
                            const b = (i < n - 1 ? alturas[i + 1] : alturas[i]) / 2;
                            const x0 = i * SEG + (i === 0 ? 0 : GAP);
                            const x1 = (i + 1) * SEG - (i === n - 1 ? 0 : GAP);
                            const zerado = !(Number(s.value) > 0);
                            return (
                                <polygon
                                    key={s.label ?? i}
                                    points={`${x0},${cy - a} ${x1},${cy - b} ${x1},${cy + b} ${x0},${cy + a}`}
                                    fill={zerado ? T.track : s.color}
                                >
                                    <title>{`${s.label}: ${formatValue(s.value) ?? '—'}`}</title>
                                </polygon>
                            );
                        })}
                    </svg>
                    {/* Rótulos em HTML (não no SVG esticado, que distorceria o texto).
                        Nome vertical completo quando cabe; senão só o número na
                        horizontal; senão nada — a legenda abaixo sempre cobre tudo. */}
                    <div className="absolute inset-0 flex pointer-events-none">
                        {validos.map((s, i) => {
                            const zerado = !(Number(s.value) > 0);
                            const alturaMedia = (alturas[i] + (i < n - 1 ? alturas[i + 1] : alturas[i])) / 2;
                            const textoCompleto = `${s.label}: ${formatValue(s.value)}`;
                            // ~6.2px por caractere a 11px de fonte
                            const cabeVertical = !zerado && textoCompleto.length * 6.2 <= alturaMedia - 10;
                            const cabeNumero = !zerado && !cabeVertical && alturaMedia >= 16;
                            return (
                                <div key={s.label ?? i} className="flex-1 min-w-0 flex items-center justify-center">
                                    {cabeVertical && (
                                        <span
                                            className="text-[11px] font-bold whitespace-nowrap"
                                            style={{
                                                writingMode: 'vertical-rl',
                                                transform: 'rotate(180deg)',
                                                color: corTextoSobre(s.color),
                                            }}
                                        >
                                            {textoCompleto}
                                        </span>
                                    )}
                                    {cabeNumero && (
                                        <span
                                            className="text-[11px] font-extrabold tabular-nums truncate px-0.5"
                                            style={{ color: corTextoSobre(s.color) }}
                                        >
                                            {formatValue(s.value)}
                                        </span>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
            {showLegend && (
                <div className="flex flex-wrap gap-x-4 gap-y-2 mt-3.5">
                    {validos.map((s, i) => (
                        <span key={s.label ?? i} className="flex items-center gap-1.5 text-[11.5px]">
                            <span className="w-2.5 h-2.5 rounded-[3px] flex-shrink-0" style={{ background: s.color }} />
                            <span className="font-semibold" style={{ color: T.label }}>
                                {s.label}
                            </span>
                            <span className="font-extrabold tabular-nums" style={{ color: T.text }}>
                                {formatValue(s.value)}
                            </span>
                            {s.conversao !== null && s.conversao !== undefined && (
                                <span className="font-semibold tabular-nums" style={{ color: T.muted }}>
                                    → {fmtPct(s.conversao, 0)}
                                </span>
                            )}
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
}

// --------------------------------------------------------------- containers

export function PageHead({ title, subtitle, children }) {
    return (
        <div className={D.pageHead}>
            <div>
                <div className={D.pageTitle}>{title}</div>
                {subtitle && <div className={D.pageSubtitle}>{subtitle}</div>}
            </div>
            {children && <div className="flex items-center gap-2.5 flex-shrink-0">{children}</div>}
        </div>
    );
}

/**
 * "há 40s" / "há 3min" a partir do timestamp do último fetch concluído.
 * Recalcula sozinho a cada 15s para o rótulo não congelar na tela.
 */
export function useAgoLabel(timestamp) {
    const [, tick] = useState(0);

    useEffect(() => {
        if (!timestamp) return undefined;
        const id = setInterval(() => tick((n) => n + 1), 15000);
        return () => clearInterval(id);
    }, [timestamp]);

    if (!timestamp) return null;
    const secs = Math.max(0, Math.round((Date.now() - timestamp) / 1000));
    if (secs < 60) return `há ${secs}s`;
    const mins = Math.round(secs / 60);
    if (mins < 60) return `há ${mins}min`;
    return `há ${Math.round(mins / 60)}h`;
}

/** Ponto verde + "atualizado há Xs". Só aparece quando há um fetch concluído. */
export function LiveDot({ since }) {
    if (!since) return null;
    return (
        <div className="flex items-center gap-[7px] text-[12px] font-semibold" style={{ color: T.label }}>
            <span
                className="w-[7px] h-[7px] rounded-full"
                style={{ background: T.okFill, boxShadow: '0 0 0 3px rgba(22,163,74,0.16)' }}
            />
            atualizado {since}
        </div>
    );
}

/**
 * Card de KPI primário. Máx. 3 por aba.
 * `value` já formatado; `note` é o denominador/contexto ao lado do número.
 * `onOpenDetails` abre modal/drawer com drill-down do indicador.
 */
export function KpiCard({
    label,
    labelTooltip,
    badge,
    badgeTone,
    tone = 'neutral',
    value,
    note,
    deltaValue,
    deltaGoodWhenDown,
    loading,
    children,
    onOpenDetails,
    detailsLabel = 'Ver detalhes',
}) {
    const t = TONES[tone] || TONES.neutral;
    const hasValue = value !== null && value !== undefined && value !== '';
    return (
        <div className={D.kpiCard} style={{ borderColor: t.border, boxShadow: t.shadow }}>
            <div className="flex items-center justify-between gap-2 min-h-[20px]">
                <div className="flex items-center gap-1.5 min-w-0">
                    <div className={D.kpiLabel}>{label}</div>
                    {labelTooltip && (
                        <TooltipProvider delayDuration={150}>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <button
                                        type="button"
                                        className="flex-shrink-0 text-[#a0a0af] hover:text-[#6b6b7b] transition-colors"
                                        aria-label="Mais informações"
                                    >
                                        <Iconify icon="eva:info-outline" width={15} />
                                    </button>
                                </TooltipTrigger>
                                <TooltipContent side="top" className="max-w-[280px] text-xs leading-relaxed">
                                    {labelTooltip}
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    )}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                    {badge && <Badge tone={badgeTone || tone}>{badge}</Badge>}
                    {onOpenDetails && !loading && (
                        <button
                            type="button"
                            onClick={onOpenDetails}
                            className={`${D.link} !text-[11.5px] whitespace-nowrap`}
                        >
                            {detailsLabel}
                        </button>
                    )}
                </div>
            </div>

            {loading ? (
                <div className="mt-3.5 h-[46px] w-2/3 rounded-lg animate-pulse" style={{ background: T.track }} />
            ) : (
                <div className="flex items-end gap-3 mt-3.5 flex-wrap">
                    <div className={D.kpiValue} style={{ color: hasValue ? t.value : T.empty }}>
                        {hasValue ? value : '—'}
                    </div>
                    {deltaValue !== undefined && deltaValue !== null && (
                        <div className="pb-1.5">
                            <Delta value={deltaValue} goodWhenDown={deltaGoodWhenDown} />
                        </div>
                    )}
                    {note && (
                        <div className="pb-[7px] text-[13px] font-semibold" style={{ color: T.label }}>
                            {note}
                        </div>
                    )}
                </div>
            )}

            {children && <div className="mt-[18px]">{loading ? <SkeletonBar height={34} /> : children}</div>}
        </div>
    );
}

/**
 * Faixa de KPIs secundários: um card só, dividido por separadores.
 * `items`: [{ label, value, note, deltaValue, deltaGoodWhenDown, tone }]
 */
export function StatStrip({ items = [], loading, className = '' }) {
    const cols = items.length || 1;
    return (
        <div className={`${D.strip} ${className}`} style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}>
            {items.map((item, i) => {
                const t = TONES[item.tone || 'neutral'] || TONES.neutral;
                const hasValue = item.value !== null && item.value !== undefined && item.value !== '';
                return (
                    <div
                        key={i}
                        className={D.stripCell}
                        style={i < items.length - 1 ? { borderRight: `1px solid ${T.divider}` } : undefined}
                    >
                        <div className={D.stripLabel}>{item.label}</div>
                        {loading ? (
                            <div className="mt-2 h-[26px] w-16 rounded animate-pulse" style={{ background: T.track }} />
                        ) : (
                            <div className="flex items-baseline gap-2 mt-[7px] flex-wrap">
                                <div className={D.stripValue} style={{ color: hasValue ? t.value : T.empty }}>
                                    {hasValue ? item.value : '—'}
                                </div>
                                {item.deltaValue !== undefined && item.deltaValue !== null && (
                                    <Delta value={item.deltaValue} goodWhenDown={item.deltaGoodWhenDown} size={12.5} />
                                )}
                                {item.note && (
                                    <span className="text-[12.5px] font-semibold" style={{ color: T.label }}>
                                        {item.note}
                                    </span>
                                )}
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
}

/** Bloco com gráfico/tabela + frase de leitura opcional no rodapé. */
export function Block({ title, subtitle, right, insight, className = '', bodyClassName = '', children }) {
    return (
        <div className={`${D.blockCard} ${className}`}>
            {(title || right) && (
                <div className="flex items-end justify-between gap-4 flex-wrap">
                    <div>
                        {title && <div className={D.blockTitle}>{title}</div>}
                        {subtitle && <div className={D.blockSubtitle}>{subtitle}</div>}
                    </div>
                    {right && <div className="flex-shrink-0">{right}</div>}
                </div>
            )}
            <div className={bodyClassName || (title ? 'mt-[22px]' : '')}>{children}</div>
            {insight && <div className={D.insight}>{insight}</div>}
        </div>
    );
}

/** Faixa de alerta: achado que exige decisão. */
export function AlertBand({ tone = 'warn', children }) {
    const t = TONES[tone] || TONES.warn;
    return (
        <div className="flex gap-3 items-start bg-white rounded-[14px] px-5 py-4 border" style={{ borderColor: t.border }}>
            <div
                className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-[13px] font-extrabold"
                style={{ background: t.badgeBg, color: t.badgeText }}
            >
                !
            </div>
            <div className="text-[12.5px] font-medium leading-[1.55]" style={{ color: T.text2 }}>
                {children}
            </div>
        </div>
    );
}

export function Strong({ children }) {
    return (
        <strong className="font-bold" style={{ color: T.text }}>
            {children}
        </strong>
    );
}

// ------------------------------------------------------------------ estados

export function SkeletonBar({ height = 12, width = '100%' }) {
    return <div className="rounded-lg animate-pulse" style={{ height, width, background: T.track }} />;
}

export function SkeletonBlock({ height = 220 }) {
    return (
        <div className="bg-white border rounded-[14px] p-[22px]" style={{ borderColor: T.border }}>
            <SkeletonBar height={16} width="35%" />
            <div className="mt-5">
                <SkeletonBar height={height} />
            </div>
        </div>
    );
}

/** Estado vazio: texto direto, sem ilustração. */
export function Empty({ children = 'Sem dados no período selecionado.', className = '' }) {
    return (
        <div className={`py-8 text-center text-[12.5px] font-medium ${className}`} style={{ color: T.empty }}>
            {children}
        </div>
    );
}

export { T, TONES, D as DESIGN };
